/**
 * HERO SECTION — Rediseño B2B SaaS
 *
 * Dirección Estética: Minimalista Operacional
 * Fuentes: Syne (display) + Inter (body) — definidas en globals.css
 * Paleta: Slate-950 fondo + Blue-600 CTA + Emerald-400 acento
 * Motion: clases animate-* de tailwind.config.js + delays en globals.css
 * Layout: Asimétrico 55/45 — copy dominante + visual contenida
 *
 * DEPENDENCIAS:
 *   - globals.css  → @import fuentes, keyframes, clases .animate-* y .delay-*
 *   - tailwind.config.js → animaciones registradas, colores brand, sombras custom
 */

import React from 'react';
import { TrendingDown, Calendar, ArrowRight, AlertTriangle, CheckCircle, Zap } from 'lucide-react';

// ─── Sub-componentes reutilizables ────────────────────────────────────────────

const MetricCard = ({ label, value, status, variant }) => {
  const variants = {
    danger: {
      bar: 'bg-red-500',
      value: 'text-red-600',
      bg: 'bg-red-50/80',
      icon: <AlertTriangle size={12} className="text-red-500" />,
    },
    success: {
      bar: 'bg-emerald-500',
      value: 'text-emerald-600',
      bg: 'bg-emerald-50/80',
      icon: <CheckCircle size={12} className="text-emerald-500" />,
    },
    warning: {
      bar: 'bg-amber-400',
      value: 'text-amber-600',
      bg: 'bg-amber-50/80',
      icon: <Zap size={12} className="text-amber-500" />,
    },
  };

  const v = variants[variant];

  return (
    <div className={`relative flex items-center gap-3 px-4 py-3 rounded-xl ${v.bg} border border-white/60`}>
      {/* Barra lateral de color */}
      <div className={`absolute left-0 top-3 bottom-3 w-[3px] ${v.bar} rounded-full`} />
      
      <div className="flex-1 min-w-0 pl-2">
        <p className="text-[11px] font-medium text-slate-500 truncate tracking-wide uppercase">
          {label}
        </p>
        <p className={`text-[17px] font-bold ${v.value} leading-tight mt-0.5`}>
          {value}
        </p>
      </div>
      
      <div className="flex items-center gap-1 shrink-0">
        {v.icon}
        <span className="text-[10px] font-semibold text-slate-400">{status}</span>
      </div>
    </div>
  );
};

const StatBadge = ({ number, label }) => (
  <div className="flex flex-col items-center gap-1">
    <span className="text-2xl font-bold text-emerald-400 leading-none tracking-tight">
      {number}
    </span>
    <span className="text-[11px] text-slate-400 font-medium text-center leading-tight uppercase tracking-wider">
      {label}
    </span>
  </div>
);

// ─── Dashboard Visual ─────────────────────────────────────────────────────────

const DashboardMockup = () => (
  <div className="animate-float relative">
    {/* Glow ambiental detrás del card */}
    <div className="absolute -inset-4 bg-blue-500/10 rounded-3xl blur-2xl pointer-events-none" />

    {/* Card principal del dashboard */}
    <div className="relative bg-white rounded-2xl shadow-card border border-slate-100 overflow-hidden">
      
      {/* Top bar del software */}
      <div className="flex items-center gap-2 px-5 py-3.5 bg-slate-900 border-b border-slate-800">
        <div className="flex gap-1.5">
          <span className="w-3 h-3 rounded-full bg-red-400/70" />
          <span className="w-3 h-3 rounded-full bg-amber-400/70" />
          <span className="w-3 h-3 rounded-full bg-emerald-400/70" />
        </div>
        <div className="flex-1 mx-3 h-5 bg-slate-700/60 rounded-md" />
        <div className="w-6 h-6 rounded-md bg-blue-500/30 flex items-center justify-center">
          <span className="text-[8px] text-blue-300 font-bold">R</span>
        </div>
      </div>

      {/* Header del módulo */}
      <div className="flex items-center justify-between px-5 py-4 bg-slate-50 border-b border-slate-100">
        <div>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
            Rentabilidad por Viaje
          </p>
          <p className="text-sm font-bold text-slate-800 mt-0.5">
            Semana 28 · Julio 2025
          </p>
        </div>
        <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" />
          <span className="text-[11px] font-semibold text-emerald-700">En vivo</span>
        </div>
      </div>

      {/* Métricas */}
      <div className="p-4 space-y-2.5 bg-white">
        <MetricCard
          label="Lima → Arequipa"
          value="−S/ 850"
          status="Pérdida"
          variant="danger"
        />
        <MetricCard
          label="Lima → Trujillo"
          value="+S/ 1,240"
          status="Rentable"
          variant="success"
        />
        <MetricCard
          label="Camión VXI-789"
          value="S/ 120 / viaje"
          status="Riesgo"
          variant="warning"
        />
      </div>

      {/* Footer del card */}
      <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
        <span className="text-[11px] text-slate-400">
          Actualizado hace 2 min
        </span>
        <button className="text-[11px] font-semibold text-blue-600 flex items-center gap-1 hover:text-blue-700 transition-colors">
          Ver reporte completo <ArrowRight size={10} />
        </button>
      </div>
    </div>

    {/* Floating notification */}
    <div className="animate-slide-in-right delay-800 absolute -top-4 -right-4 bg-white rounded-xl shadow-float border border-slate-100 px-4 py-3 flex items-center gap-3 min-w-[200px]">
      <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
        <AlertTriangle size={14} className="text-red-500" />
      </div>
      <div>
        <p className="text-[11px] font-bold text-slate-800 leading-tight">
          Alerta de Rentabilidad
        </p>
        <p className="text-[10px] text-slate-500 mt-0.5">
          3 viajes con pérdida esta semana
        </p>
      </div>
    </div>
  </div>
);

// ─── Hero Principal ───────────────────────────────────────────────────────────

const Hero = () => {
  const scrollToForm = () => {
    document.getElementById('formulario-contacto')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToHow = () => {
    document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <section className="relative bg-slate-950 text-white overflow-hidden mt-[70px]">

        {/* ── Fondo: grid sutil + radial ── */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              radial-gradient(circle at 60% 0%, rgba(37, 99, 235, 0.12) 0%, transparent 55%),
              radial-gradient(circle at 5% 80%, rgba(16, 185, 129, 0.06) 0%, transparent 40%),
              linear-gradient(rgba(148, 163, 184, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(148, 163, 184, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: 'auto, auto, 48px 48px, 48px 48px',
          }}
        />

        {/* ── Contenido principal ── */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">

            {/* ══ COLUMNA IZQUIERDA: Copy ══ */}
            <div className="flex flex-col gap-6">

              {/* Badge */}
              <div className="animate-fade-up delay-0 inline-flex items-center gap-2 self-start bg-white/[0.05] border border-white/10 rounded-full px-4 py-2 backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-dot shrink-0" />
                <span className="text-[12px] font-medium text-slate-300 tracking-wide">
                  Software especializado para transporte de carga en Perú
                </span>
              </div>

              {/* H1 */}
              <h1 className="animate-fade-up delay-80 text-4xl sm:text-5xl lg:text-[3.4rem] font-bold leading-[1.08] tracking-[-0.03em] text-white font-display">
                ¿Cuánto dinero estás{' '}
                <span className="relative inline-block text-red-400">
                  perdiendo
                  <svg
                    className="absolute -bottom-1 left-0 w-full"
                    viewBox="0 0 200 8"
                    fill="none"
                    preserveAspectRatio="none"
                    style={{ height: '5px' }}
                  >
                    <path
                      d="M1 5.5 Q50 1 100 5.5 Q150 10 199 5.5"
                      stroke="rgba(248,113,113,0.45)"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      fill="none"
                    />
                  </svg>
                </span>
                {' '}en cada viaje sin darte cuenta?
              </h1>

              {/* Subtítulo */}
              <p className="animate-fade-up delay-160 text-lg text-slate-400 leading-[1.7] max-w-[480px] font-light">
                Control de rentabilidad en tiempo real para empresas de transporte.
                Descubre qué viajes, vehículos y clientes te están haciendo{' '}
                <span className="text-slate-200 font-medium">perder dinero</span>.
              </p>

              {/* Alerta de problema */}
              <div className="animate-fade-up delay-240 flex items-start gap-3 bg-red-950/40 border border-red-900/40 rounded-xl px-4 py-3.5">
                <TrendingDown
                  size={18}
                  className="text-red-400 shrink-0 mt-0.5"
                />
                <p className="text-sm text-slate-300 leading-[1.6]">
                  <strong className="text-white font-semibold">
                    El 70% de empresas de transporte
                  </strong>{' '}
                  no conoce su rentabilidad real por viaje hasta fin de mes… cuando ya es tarde.
                </p>
              </div>

              {/* CTAs */}
              <div className="animate-fade-up delay-320 flex flex-col sm:flex-row gap-3 pt-1">
                {/* CTA primario */}
                <button
                  onClick={scrollToForm}
                  className="group flex items-center justify-center gap-2.5 bg-blue-600 hover:bg-blue-500 text-white font-semibold text-[15px] px-7 py-3.5 rounded-xl transition-all duration-200 hover:shadow-blue-glow hover:-translate-y-0.5 active:translate-y-0 font-body"
                >
                  <Calendar size={17} className="shrink-0" />
                  Agendar Diagnóstico Gratuito
                </button>

                {/* CTA secundario */}
                <button
                  onClick={scrollToHow}
                  className="group flex items-center justify-center gap-2 text-slate-300 hover:text-white font-medium text-[15px] px-5 py-3.5 rounded-xl border border-white/10 hover:border-white/20 transition-all duration-200 hover:bg-white/[0.04]"
                >
                  Ver cómo funciona
                  <ArrowRight
                    size={15}
                    className="transition-transform duration-200 group-hover:translate-x-0.5"
                  />
                </button>
              </div>

              {/* Proof stats */}
              <div className="animate-fade-up delay-400 flex items-center gap-6 pt-4 border-t border-white/[0.08]">
                <StatBadge number="15 min" label="Diagnóstico virtual" />
                <div className="w-px h-8 bg-white/10" />
                <StatBadge number="100%" label="Adaptado a Perú" />
                <div className="w-px h-8 bg-white/10" />
                <StatBadge number="2–10" label="Vehículos ideales" />
              </div>
            </div>

            {/* ══ COLUMNA DERECHA: Dashboard ══ */}
            <div className="animate-fade-up delay-160 relative hidden lg:block">
              <DashboardMockup />
            </div>

          </div>
        </div>

        {/* ── Separador wave ── */}
        <div className="relative h-14 overflow-hidden">
          <svg
            viewBox="0 0 1200 56"
            preserveAspectRatio="none"
            className="absolute bottom-0 w-full h-full"
          >
            <path
              d="M0,28 C300,56 900,0 1200,28 L1200,56 L0,56 Z"
              fill="#f8fafc"
            />
          </svg>
        </div>
      </section>
    </>
  );
};

export default Hero;